import React from 'react';
import '../App.css';

/**
 * DeadlineReminder - Two-tier deadline popup system
 * "urgent" variant: Fullscreen red popup for TODAY's deadlines
 * "weekly" variant: Yellow modal for THIS WEEK's deadlines (excluding today)
 */
function DeadlineReminder({
    // New API
    isTodayOpen,
    isWeekOpen,
    todayItems = [],
    weekItems = [],
    onCloseTodayPopup,
    onCloseWeekPopup,
    onDismissToday,
    // Legacy API
    isOpen,
    onClose,
    items = [],
    contextKey,
    type = 'task'
}) {
    // Detect which API is being used
    const isNewAPI = isTodayOpen !== undefined;

    if (isNewAPI) {
        return (
            <>
                {isTodayOpen && (
                    <TodayUrgentPopup
                        items={todayItems}
                        type={type}
                        onClose={onCloseTodayPopup}
                        onDismiss={onDismissToday}
                    />
                )}
                {isWeekOpen && (
                    <WeeklyPopup
                        items={weekItems}
                        type={type}
                        onClose={onCloseWeekPopup}
                        onDismiss={onDismissToday}
                    />
                )}
            </>
        );
    }

    // Legacy fallback
    if (!isOpen) return null;
    return (
        <LegacyReminder
            isOpen={isOpen}
            onClose={onClose}
            items={items}
            contextKey={contextKey}
            type={type}
        />
    );
}

// ========================
// POPUP 1: VENCIMENTOS DO DIA (URGENTE)
// ========================
function TodayUrgentPopup({ items, type, onClose, onDismiss }) {
    return (
        <div className="reminder-urgent-overlay">
            <div className="reminder-urgent-container">
                {/* Animated urgency bars */}
                <div className="reminder-urgent-bar reminder-urgent-bar-top"></div>
                <div className="reminder-urgent-bar reminder-urgent-bar-bottom"></div>

                {/* Icon pulse */}
                <div className="reminder-urgent-icon">🚨</div>

                <h1 className="reminder-urgent-title">
                    VENCIMENTOS DE HOJE
                </h1>

                {items.length === 0 ? (
                    <div className="reminder-urgent-empty">
                        <div className="reminder-urgent-empty-icon">✅</div>
                        <p>Nenhum vencimento para hoje!</p>
                    </div>
                ) : (
                    <>
                        <div className="reminder-urgent-count">
                            <span className="reminder-urgent-count-number">{items.length}</span>
                            <span className="reminder-urgent-count-label">
                                {type === 'task' ? 'tarefa(s)' : 'compromisso(s)'} vencendo HOJE
                            </span>
                        </div>

                        <div className="reminder-urgent-list">
                            {items.map((item, index) => (
                                <div key={item.id || index} className="reminder-urgent-item">
                                    <span className="reminder-urgent-item-time">
                                        {type === 'event' ? (item._sortTime || item.horario || '--:--') : '⏰'}
                                    </span>
                                    <span className="reminder-urgent-item-text">
                                        {type === 'task' ? item.text : (item.title || item.descricao || item.auditoria)}
                                    </span>
                                    {(item.responsavel || item.auditor || item.setor) && (
                                        <span className="reminder-urgent-item-meta">
                                            {item.responsavel || item.auditor} {item.setor ? `(${item.setor})` : ''}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </>
                )}

                <div className="reminder-urgent-actions">
                    <button className="reminder-urgent-btn-dismiss" onClick={onDismiss}>
                        ✓ Ciente (não mostrar hoje)
                    </button>
                    <button className="reminder-urgent-btn-close" onClick={onClose}>
                        Fechar
                    </button>
                </div>
            </div>
        </div>
    );
}

// ========================
// POPUP 2: VENCIMENTOS DA SEMANA (ATENÇÃO)
// ========================
function WeeklyPopup({ items, type, onClose, onDismiss }) {
    const getDayLabel = (item) => {
        const dateStr = type === 'task' ? item.dataFim : (item.start || item.data);
        if (!dateStr) return '';
        const d = new Date(typeof dateStr === 'string' ? dateStr + 'T00:00:00' : dateStr);
        const dayNames = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
        return `${dayNames[d.getDay()]} ${d.toLocaleDateString('pt-BR')}`;
    };

    return (
        <div className="modal-overlay" style={{ zIndex: 3000 }}>
            <div className="reminder-weekly-container">
                {/* Header */}
                <div className="reminder-weekly-header">
                    <div className="reminder-weekly-header-icon">⚠️</div>
                    <h2 className="reminder-weekly-title">Vencimentos da Semana</h2>
                    <button className="reminder-weekly-close-btn" onClick={onClose}>×</button>
                </div>

                {/* Body */}
                <div className="reminder-weekly-body">
                    {items.length === 0 ? (
                        <div className="reminder-weekly-empty">
                            <div style={{ fontSize: '2.5rem', marginBottom: '10px' }}>🎉</div>
                            <p>Nenhum vencimento para esta semana!</p>
                        </div>
                    ) : (
                        <>
                            <p className="reminder-weekly-subtitle">
                                Você tem <strong>{items.length}</strong> {type === 'task' ? 'tarefa(s)' : 'compromisso(s)'} nos próximos dias:
                            </p>
                            <div className="reminder-weekly-list">
                                {items.map((item, index) => (
                                    <div key={item.id || index} className="reminder-weekly-item">
                                        <div className="reminder-weekly-item-day">
                                            {getDayLabel(item)}
                                        </div>
                                        <div className="reminder-weekly-item-info">
                                            <span className="reminder-weekly-item-time">
                                                {type === 'event' ? (item._sortTime || item.horario || '--:--') : '📋'}
                                            </span>
                                            <span className="reminder-weekly-item-text">
                                                {type === 'task' ? item.text : (item.title || item.descricao || item.auditoria)}
                                            </span>
                                        </div>
                                        {(item.responsavel || item.auditor || item.setor) && (
                                            <div className="reminder-weekly-item-meta">
                                                {item.responsavel || item.auditor} {item.setor ? `(${item.setor})` : ''}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="reminder-weekly-actions">
                    <button className="reminder-weekly-btn-dismiss" onClick={onDismiss}>
                        ✓ Ciente (não mostrar hoje)
                    </button>
                    <button className="reminder-weekly-btn-close" onClick={onClose}>
                        Fechar
                    </button>
                </div>
            </div>
        </div>
    );
}

// ========================
// LEGACY REMINDER (backward compat)
// ========================
function LegacyReminder({ isOpen, onClose, items, contextKey, type }) {
    if (!isOpen) return null;

    const handleDismissToday = () => {
        const todayStr = new Date().toISOString().split('T')[0];
        const dismissedKey = `reminder_dismissed_${contextKey}_${todayStr}`;
        localStorage.setItem(dismissedKey, 'true');
        onClose();
    };

    return (
        <div className="modal-overlay" style={{ zIndex: 3000 }}>
            <div className="modal-content reminder-modal">
                <div className="reminder-header">
                    <h3>🔔 Lembretes do Dia</h3>
                </div>
                <div className="reminder-body">
                    {items.length === 0 ? (
                        <p style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                            Não há vencimentos para hoje ou amanhã! 🎉
                        </p>
                    ) : (
                        <>
                            <p>Você tem <strong>{items.length}</strong> {type === 'task' ? 'tarefas' : 'compromissos'} para hoje ou amanhã:</p>
                            <ul className="reminder-list">
                                {items.map((item, index) => (
                                    <li key={item.id || index} className="reminder-item">
                                        <span className="reminder-date">
                                            {type === 'task' ?
                                                new Date(item.dataFim + 'T00:00:00').toLocaleDateString('pt-BR') :
                                                new Date(item.start).toLocaleDateString('pt-BR')}
                                        </span>
                                        <span className="reminder-text">
                                            {type === 'task' ? item.text : item.title}
                                        </span>
                                        {(item.responsavel || item.setor) && (
                                            <span className="reminder-meta">
                                                {item.responsavel} {item.setor ? `(${item.setor})` : ''}
                                            </span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </div>
                <div className="reminder-actions">
                    <button className="btn-save" onClick={handleDismissToday}>
                        Ok, já li (Não mostrar hoje)
                    </button>
                    <button className="btn-cancel" onClick={onClose}>
                        Fechar
                    </button>
                </div>
            </div>
        </div>
    );
}

export default DeadlineReminder;
